import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';

export const tableV2CompactPaginationRowLimit = 10;

export const buildTableV2ResetKey = ({
  fixedFilters,
  search,
  sorts,
}: {
  fixedFilters?: GridQueryInput['filters'];
  search?: string;
  sorts: GridQueryInput['sorts'];
}): string =>
  JSON.stringify({
    filters: fixedFilters ?? [],
    search: search?.trim() ? search.trim() : null,
    sorts,
  });

const resolveTableV2SingularEntityLabel = (entityLabel: string): string => {
  if (entityLabel.endsWith('ies')) {
    return `${entityLabel.slice(0, -3)}y`;
  }

  if (entityLabel.endsWith('s')) {
    return entityLabel.slice(0, -1);
  }

  return entityLabel;
};

export const formatTableV2DisplayedRowsLabel = ({
  displayedFrom,
  displayedTo,
  entityLabel,
  entitySingularLabel,
  pageSize,
  total,
}: {
  displayedFrom: number;
  displayedTo: number;
  entityLabel: string;
  entitySingularLabel?: string;
  pageSize: number;
  total: number;
}): string => {
  if (total <= pageSize) {
    const resolvedEntityLabel =
      total === 1
        ? (entitySingularLabel ?? resolveTableV2SingularEntityLabel(entityLabel))
        : entityLabel;
    return `Showing ${String(total)} ${resolvedEntityLabel}`;
  }

  return `Showing ${String(displayedFrom)} to ${String(displayedTo)} of ${String(total)} ${entityLabel}`;
};

export const resolveTableV2PageSize = (pageSize: number): number =>
  Math.max(tableV2CompactPaginationRowLimit, pageSize);

export const resolveTableV2PageSizeOptions = (pageSizeOptions: number[]): number[] =>
  Array.from(new Set(pageSizeOptions.map(resolveTableV2PageSize))).sort(
    (left, right) => left - right,
  );

export const shouldShowTableV2PaginationControls = (total: number): boolean =>
  total > tableV2CompactPaginationRowLimit;

export const shouldFillTableV2Rows = (total: number, minimumVisibleRows: number): boolean =>
  total >= minimumVisibleRows;

export const resolveTableV2SkeletonRowCount = ({
  pageSize,
  placeholderData,
  rowCount,
}: {
  pageSize: number;
  placeholderData: boolean;
  rowCount: number;
}): number => (placeholderData && rowCount > 0 ? rowCount : pageSize);

export const resolveTableV2FillerRowCount = ({
  bodyContentRowCount,
  minimumVisibleRows,
  placeholderData,
  shouldFillRows,
  showSkeletonRows,
}: {
  bodyContentRowCount: number;
  minimumVisibleRows: number;
  placeholderData: boolean;
  shouldFillRows: boolean;
  showSkeletonRows: boolean;
}): number => {
  if (!shouldFillRows) return 0;
  if (showSkeletonRows && !placeholderData) return 0;
  return Math.max(0, minimumVisibleRows - bodyContentRowCount);
};

export const shouldShowTableV2SkeletonRows = ({
  authLoading,
  forceLoading,
  hasData,
  placeholderData,
  queryFetching,
  queryLoading,
}: {
  authLoading: boolean;
  forceLoading: boolean;
  hasData: boolean;
  placeholderData: boolean;
  queryFetching: boolean;
  queryLoading: boolean;
}): boolean =>
  forceLoading || queryLoading || placeholderData || (!hasData && (queryFetching || authLoading));
